import { ERROR_CODES, PRINTER_TYPES, isValidIPAddress, isValidCmd } from "./Utils";
import PrinterError from "./PrinterError";
// Dynamically assigns the platform-specific printer adapter based on the environment
let printerAdapter;

(function loadPrinterModule(runtime) {
    // Dynamically assigns the platform-specific printer adapter based on the environment
    const getModuleName = () => {
        switch (runtime) {
            case 'electron':
                return 'ElectronPrinter.js';
            case 'cordova':
                return 'CordovaPrinter.js';
            default:
                // Unsupported Environment
                console.error(`Unsupported environment: ${runtime}`);
                return null;
        }
    };

    const moduleName = getModuleName();
    if (!moduleName) {
        console.error('Unable to find printer module');
        return;
    }

    import(`./${moduleName}`)
        .then((module) => {
            // Assign the printer adapter from the imported module
            printerAdapter = module.default;
        })
        .catch((err) =>
            console.error('Failed to load printer module:', err)
        );
})(process.env.VUE_APP_RUNTIME);


const invokePrinter = async (req) => {
    // Validate the request object
    if (!req || typeof req !== "object" || !req.command) {
        throw new PrinterError(ERROR_CODES.INVALID_INPUT, "Invalid request object.");
    }
    if (!isValidCmd(req.command)) {
        throw new PrinterError(ERROR_CODES.INVALID_INPUT, "Command must be a Uint8Array.");
    }

    try {
        switch (req.printer_type) {

            case "ip": // Handle IP-based printers
                // Validate input parameters
                var printerIP = req.printer_ip;
                if (!printerIP || !isValidIPAddress(printerIP)) {
                    throw new PrinterError(ERROR_CODES.INVALID_INPUT, "Invalid printer IP address.");
                }

                return await printerAdapter.sendToNetworkPrinter(printerIP, req.command);
            // Handle usb printer
            case 'usb':
                // Validate input
                if (!req.printer_id) {
                    throw new PrinterError(ERROR_CODES.INVALID_INPUT, "Invalid printer ID.");
                }

                return await printerAdapter.sendToUsbPrinter(req.printer_id, req.command)
            case 'bluetooth': // Handle Bluetooth printers
                // Validate input
                if (!req.printer_id) {
                    throw new PrinterError(ERROR_CODES.INVALID_INPUT, "Invalid printer ID.");
                }

                return await printerAdapter.sendToBluetoothPrinter(req.printer_id, req.command)

            default:
                throw new PrinterError(
                    ERROR_CODES.INVALID_INPUT,
                    `Unsupported printer type: ${req.printer_type}`
                );
        }

    } catch (error) {
        // console.error('Failed to print job:', req, error);
        throw error;
    }
}


// Main Printer object
// Provides a unified API for printing, regardless of the platform
const Printer = {
    /**
     * Prints to multiple printers or performs multiple print operations in sequence.
     *
     * @param {Object} printDetail - printing details.
     * object should contain:
     *  - {string} print_type: Type of printer ('ip', 'usb', 'bluetooth').
     *  - {string} [ip_address]: IP address for network printers (required for 'ip').
     *  - {string} command: Commands to send to the printer.
     * @throws Will throw an error if the print type is unsupported or adapter is uninitialized.
     * @returns {Promise<void>} Resolves when all print operations are successful.
     */
    async print(req) {
        if (!printerAdapter) {
            throw new Error(
                'Platform printer adapter is not initialized or unsupported environment'
            );
        }
        return invokePrinter(req)
    },
    /**
     * Fetches available USB devices.
     *
     * @returns {Promise<Array<Object>>} Resolves with a list of USB devices.
     */
    async getUsbDevices() {
        if (!printerAdapter) {
            throw new Error(
                'Platform printer adapter is not initialized or unsupported environment'
            );
        }
        return printerAdapter.getUsbDevices();
    },

    /**
     * Fetches available Bluetooth devices.
     *
     * @returns {Promise<Array<Object>>} Resolves with a list of Bluetooth devices.
     */
    async getBluetoothDevices() {
        if (!printerAdapter) {
            throw new Error(
                'Platform printer adapter is not initialized or unsupported environment'
            );
        }
        return printerAdapter.getBluetoothDevices();
    },

    getSupportedPrinterTypes() {
        if (!printerAdapter) {
            // throw new Error(
            //     'Platform printer adapter is not initialized or unsupported environment'
            // );
            return [
                PRINTER_TYPES.NO_PRINT,
                PRINTER_TYPES.NETWORK
            ]
        }
        
        return printerAdapter.getSupportedPrinterTypes();
    }
};

// Export the Printer object for use in other modules
export default Printer;